/**
 * Chat Store - Ask Blurp.ie Sidebar State
 */

import { create } from 'zustand';
import type { ChatItem, ChatMessage, SystemAlert, AlertSeverity } from '@/types';

interface ChatState {
  // Messages
  messages: ChatItem[];

  // UI state
  isOpen: boolean;
  isTyping: boolean;
  unreadCount: number;

  // Input state
  inputValue: string;
  isSubmitting: boolean;

  // Actions
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  addSystemAlert: (alert: Omit<SystemAlert, 'id' | 'timestamp' | 'type'>) => void;
  setTyping: (typing: boolean) => void;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  markAllRead: () => void;
  clearMessages: () => void;
  setInputValue: (value: string) => void;
  setSubmitting: (submitting: boolean) => void;
  removeMessage: (id: string) => void;
}

// Generate unique IDs
const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const useChatStore = create<ChatState>()((set) => ({
  messages: [],
  isOpen: false,
  isTyping: false,
  unreadCount: 0,
  inputValue: '',
  isSubmitting: false,

  addMessage: (message) => {
    const newMessage: ChatMessage = {
      ...message,
      id: generateId(),
      timestamp: Date.now(),
    };

    set((state) => ({
      messages: [...state.messages, newMessage],
      unreadCount: state.isOpen ? state.unreadCount : state.unreadCount + 1,
    }));
  },

  addSystemAlert: (alert) => {
    const newAlert: SystemAlert = {
      ...alert,
      id: generateId(),
      timestamp: Date.now(),
      type: 'alert',
    };

    set((state) => ({
      messages: [...state.messages, newAlert],
      unreadCount: state.isOpen ? state.unreadCount : state.unreadCount + 1,
    }));
  },

  setTyping: (isTyping) => set({ isTyping }),

  toggleSidebar: () =>
    set((state) => ({
      isOpen: !state.isOpen,
      unreadCount: !state.isOpen ? 0 : state.unreadCount,
    })),

  openSidebar: () => set({ isOpen: true, unreadCount: 0 }),

  closeSidebar: () => set({ isOpen: false }),

  markAllRead: () => set({ unreadCount: 0 }),

  clearMessages: () => set({ messages: [] }),

  setInputValue: (inputValue) => set({ inputValue }),

  setSubmitting: (isSubmitting) => set({ isSubmitting }),

  removeMessage: (id) =>
    set((state) => ({
      messages: state.messages.filter((m) => m.id !== id),
    })),
}));

// Helper function to add environmental intelligence alerts
export function addEnvironmentalAlert(
  severity: AlertSeverity,
  title: string,
  content: string,
  options?: Partial<Omit<SystemAlert, 'id' | 'timestamp' | 'type' | 'severity' | 'title' | 'content'>>
) {
  useChatStore.getState().addSystemAlert({
    severity,
    title,
    content,
    dismissible: true,
    ...options,
  });
}

// Selectors
export const selectMessages = (state: ChatState) => state.messages;
export const selectIsOpen = (state: ChatState) => state.isOpen;
export const selectUnreadCount = (state: ChatState) => state.unreadCount;
